import { ComponentProps, Fragment, useMemo, ElementType } from "react";
import { styled } from "~/application/theme";
import { Skeleton } from "../Skeleton";
import { LazyListItem } from "./LazyListItem";

export type LazyListProps<TItem = any> = ComponentProps<typeof LazyListRoot> & {
  as?: ElementType;
  quoteUrl?: string;
  isLoading?: boolean;
  skeletonQuantity?: number;
  skeletonHeight?: number | string;
  items: TItem[] | undefined | null;
  render: (item: TItem, index: number) => JSX.Element;
  EmptyComponent?: JSX.Element;
  SkeletonComponent?: JSX.Element;
  itemAs?: ElementType;
};

export const LazyListRoot = styled("ol", {
  margin: 0,
  padding: 0,
  marginBlock: 0,
  paddingInline: 0,
  listStyle: "none",

  variants: {
    gap: {
      "4": {
        [`& > ${LazyListItem} + ${LazyListItem}`]: {
          marginTop: "$4",
        },
      },
      "5": {
        [`& > ${LazyListItem} + ${LazyListItem}`]: {
          marginTop: "$5",
        },
      },
      "6": {
        [`& > ${LazyListItem} + ${LazyListItem}`]: {
          marginTop: "$6",
        },
      },
      "8": {
        [`& > ${LazyListItem} + ${LazyListItem}`]: {
          marginTop: "$8",
        },
      },
      "10": {
        [`& > ${LazyListItem} + ${LazyListItem}`]: {
          marginTop: "$10",
        },
      },
      "12": {
        [`& > ${LazyListItem} + ${LazyListItem}`]: {
          marginTop: "$12",
        },
      },
    },
  },

  defaultVariants: {
    gap: "6",
  },
});

export const LazyList = <TItem,>({
  skeletonQuantity = 3,
  skeletonHeight = 80,
  isLoading = false,
  items,
  render,
  EmptyComponent,
  SkeletonComponent,
  itemAs = "li",
  ...props
}: LazyListProps<TItem>) => {
  const skeletonsArray = useMemo(
    () => (
      <Fragment>
        {Array(skeletonQuantity)
          .fill(null)
          .map((_, index) => (
            <LazyListItem as={itemAs} key={index}>
              {SkeletonComponent ?? <Skeleton variant="fluid" style={{ height: skeletonHeight }} />}
            </LazyListItem>
          ))}
      </Fragment>
    ),
    [skeletonQuantity, skeletonHeight, SkeletonComponent, itemAs],
  );

  const itemsArray = useMemo(
    () =>
      items?.map((item, index) => (
        <LazyListItem as={itemAs} data-cy="order-list-item" key={`lazy-list-item${index}`}>
          {render(item, index)}
        </LazyListItem>
      )),
    [items, render, itemAs],
  );

  return (
    <Fragment>
      {isLoading || !items ? (
        <LazyListRoot {...props}>{skeletonsArray}</LazyListRoot>
      ) : items.length === 0 ? (
        EmptyComponent
      ) : (
        <LazyListRoot {...props}>{itemsArray}</LazyListRoot>
      )}
    </Fragment>
  );
};

LazyList.displayName = "LazyList";
